/* eslint-disable no-unused-vars */
import Promise from 'bluebird';
import { noop } from 'underscore';
import { Observable, CallObservable } from '@bingads-webui-universal/observable';
import type {
  IEntityCacheItem,
  IObserver,
  ICacheState,
  IEntityCacheItemOptions,
  ODataEntity,
} from './types';

export class CollectionCacheItem<TData = unknown, TError = unknown> extends CallObservable<TData, TError> {
  // @aiContributed-MAPFuse-2026-06-04
  invalidate(): void {
    if (this.observers.size > 0 && this.state.status === 'PENDING') {
      return;
    }

    super.invalidate();
  }
}

class PatchEntityCacheItem<TData = unknown, TError = unknown> extends Observable<TData, TError> {
  observers: Set<IObserver<TData, TError>>;

  state: ICacheState<TData, TError>;

  constructor() {
    super((observer: IObserver<TData, TError>) => {
      this.observers.add(observer);
      if (this.state.status === 'READY') {
        observer.next(this.state.data);
      } else if (this.state.status === 'ERROR') {
        observer.error(this.state.error);
      }
      return () => {
        this.observers.delete(observer);
      };
    });
    // eslint-disable-next-line no-undef
    this.observers = new Set();
    this.state = {
      // WAITING - before any subscription
      // READY - already has data
      // ERROR - already has error
      status: 'WAITING',
      data: null,
      error: null,
    };
  }

  next(data: TData) {
    this.observers.forEach((item) => {
      item.next(data);
    });
    this.state.status = 'READY';
    this.state.data = data;
  }

  error(err: TError) {
    this.observers.forEach((item) => {
      item.error(err);
    });
    this.state.status = 'ERROR';
    this.state.error = err;
  }
}

export class EntityCacheItem<
  TData = unknown,
  TUpdate = ODataEntity,
  TError = unknown,
> extends CallObservable<TData, TError> implements IEntityCacheItem<TData, TUpdate, TError> {
  patch?: (update: TUpdate) => Promise<TData>;

  constructor({
    fetch,
    patch,
    afterSubscribe = noop,
    afterUnsubscribe = noop,
    hardResetCacheData,
  }: IEntityCacheItemOptions<TData, TUpdate, TError>) {
    super({
      fetch,
      afterSubscribe,
      afterUnsubscribe,
      hardResetCacheData,
    });
    this.patch = patch;
  }

  update(update: TUpdate) {
    const patchEntityCacheItem = new PatchEntityCacheItem<TData, TError>();

    const originalStatus = this.state.status;
    this.state.status = 'PENDING';

    // Bridge this item's patch result / errors to the returned observable.
    // IMPORTANT: capture the unsubscribe and release it once the patch settles.
    // Subscribing here adds an observer to this entity cache item; if it is
    // never released, the item stays permanently "observed" (and leaks), which
    // means a later type-level invalidation would re-fetch it via a GET-by-key
    // (an endpoint some middle tiers don't support, yielding a 404). Releasing
    // it lets the item fall back to the no-observer cache, where invalidation
    // only marks it stale instead of immediately re-fetching.
    const subscription = this.subscribe({
      next: data => patchEntityCacheItem.next(data),
      error: err => patchEntityCacheItem.error(err),
    });

    this.patch!(update)
      .then((data) => {
        this.observers.forEach((item) => {
          item.next(data);
        });
        this.state.status = 'READY';
        this.state.data = data;
      })
      .catch((err: TError) => {
        patchEntityCacheItem.error(err);
        this.state.status = originalStatus;
      })
      .finally(() => {
        subscription.unsubscribe();
      });

    return patchEntityCacheItem;
  }
}
